import { Box, Flex, Spinner, useBreakpointValue } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';

import { requestComposerFocus } from '#modules/chat/components/Chats/ChatInput/composerFocus';
import { getDiscordRoomQueryOptions } from '#modules/finder/features/posts/model/discord';
import { trackRoomChatOpened } from '#features/analytics/utils/sessionRoomFunnel';
import { CORNER_PROPS, MOBILE_ANCHOR_PROPS, type MobileAnchor } from '#features/media-window/model';
import { useCornerDrag } from '#features/media-window/useCornerDrag';
import { CloseIcon, CommentIcon, DiscordIcon, Icon, PhoneIcon, UnreadBadge } from '#features/ui';
import { IconButton } from '#features/ui/components/IconButton';
import { Tooltip } from '#features/ui/components/Tooltip';
import { floatingBarSurface } from '#features/ui/floatingBarSurface';
import { DiscordLinkAnchor } from '#features/voice/components/DiscordLinkAnchor';
import { tierCountLine } from '#features/voice/components/SessionVoiceTier/model';
import { TierMicCluster } from '#features/voice/components/SessionVoiceTier/TierCluster';
import { TierCounts } from '#features/voice/components/SessionVoiceTier/TierCounts';
import { useVoiceRoomOwner } from '#features/voice/components/VoiceRoomProvider';
import { useDiscordChannelDoor } from '#features/voice/hooks/useDiscordChannelDoor';
import { useVoiceActions } from '#features/voice/hooks/useVoiceActions';
import { useVoiceRoomState } from '#features/voice/hooks/useVoiceRoomState';
import { getVoiceRoomQueryOptions, leaveVoice } from '#features/voice/model/voice';
import { useVoiceStore } from '#features/voice/model/voiceStore';
import { mergeRosterVoice } from '#features/voice/utils/mergeRosterVoice';
import { requestVoiceMicrophone } from '#features/voice/utils/nativeVoiceSession';
import { isDefined } from '#utils/isDefined';
import type { LastSystemActor, SessionRoomEntry } from '../model/roomSessions';
import { useSessionRoomStore } from '../model/sessionRoomStore';
import {
  formatStartsIn,
  isClosedPhase,
  resolvePillVoiceSlot,
  useRoomClock,
  type SessionRoomPhase,
} from './RoomChrome';
import { RoomCompactStatus, RoomVoiceReturnIcon } from './RoomCollapsedChrome';
import { RoomModeButton } from './RoomModeButton';
import { RoomSurface } from './RoomSurface';
import { SessionRoomGameLogo } from './SessionRoomGameLogo';

type SessionRoomPillProps = {
  surface?: 'pill' | 'bar';
  /** Край полосы на телефоне, если он не тот, что в сторе (пока открыта клавиатура – верх). */
  anchor?: MobileAnchor;
  /** Край закреплён: полосу нельзя переносить. */
  pinned?: boolean;
  /**
   * Чей край двигает жест: по умолчанию общий (`mobileAnchor`), в переписке – свой
   * (`chatAnchor`). Жест считается от сохранённого края этой поверхности.
   */
  dragAnchor?: MobileAnchor;
  onDragAnchor?: (anchor: MobileAnchor) => void;
  onNodeChange?: (node: HTMLDivElement | null) => void;
  session: SessionRoomEntry;
  phase: SessionRoomPhase;
  /**
   * Развернуться в окно. Режим комнаты живёт в её сторе, поэтому обработчик необязателен: без
   * него пилюля разворачивается сама.
   */
  onExpand?: () => void;
  /**
   * Автор последней реплики. Запрос комнаты имени не отдаёт, и подставлять сюда чужое имя хуже,
   * чем показать одну реплику: имя приходит от того, кто держит ленту.
   */
  lastMessageAuthor?: string | null;
  /** О ком последняя служебная строка – см. `SessionRoomSurfaceProps`. */
  lastSystemActor?: LastSystemActor | null;
  /**
   * Перейти в переписку сессии. `null` значит «переписки ещё нет»: кнопка остаётся на месте, но
   * не ведёт – пустой слот читался бы как «чата у этой сессии не будет».
   */
  onFullscreen?: (() => void) | null;
  /** Открыть «Настройки сессии» из кебаба. Диалог живёт у хоста – см. `SessionRoomMenu`. */
  onOpenSessionSettings?: () => void;
  /**
   * Убрать комнату с экрана.
   *
   * Нужен закончившейся сессии: кебаба у неё больше нет (см. `roomMenuItems`), а закрыть
   * свёрнутую комнату было нечем вовсе - крестик живёт только в развёрнутом окне, и чтобы
   * добраться до него, человеку приходилось сперва раскрыть то, что он и хотел убрать.
   */
  onClose?: () => void;
};

export const SessionRoomPill = ({
  session,
  phase,
  onExpand,
  onClose,
  onFullscreen,
  surface = 'pill',
  onNodeChange,
  anchor,
  pinned = false,
  dragAnchor,
  onDragAnchor,
}: SessionRoomPillProps) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();

  const setMode = useSessionRoomStore((state) => state.setMode);
  const corner = useSessionRoomStore((state) => state.corner);
  const setCorner = useSessionRoomStore((state) => state.setCorner);
  const storedAnchor = useSessionRoomStore((state) => state.mobileAnchor);
  const mobileAnchor = anchor ?? storedAnchor;
  const setMobileAnchor = useSessionRoomStore((state) => state.setMobileAnchor);

  const pillRef = useRef<HTMLDivElement | null>(null);

  /** Развернуться в окно: без обработчика снаружи это просто смена режима в сторе. */
  const expand = () => (onExpand ? onExpand() : setMode('window'));

  const onGripPointerDown = useCornerDrag({
    ref: pillRef,
    canDrag: true,
    // Полоса на телефоне переезжает между нижним и верхним краем; сворачивать ей уже некуда.
    // Жест считается от сохранённого края, а не от временного: перенос вверх во время набора
    // иначе совпал бы с «уже наверху», не записался бы, и полоса уехала бы вниз с клавиатурой.
    anchor: dragAnchor ?? storedAnchor,
    onCollapse: () => undefined,
    onCorner: setCorner,
    onAnchor: onDragAnchor ?? setMobileAnchor,
    locked: pinned,
  });

  const { state, isPlatform, inThisRoom } = useVoiceRoomState({
    sessionId: session.sessionId,
    voiceMode: session.voiceMode,
    teamLimit: session.teamLimit ?? 0,
    /*
      Свёрнутая комната – единственное, что человек видит, пока он на другой странице, и дробь
      «3/5» в ней и есть ответ на «я один?». Кадр по WebSocket иногда не доезжает, и без опроса
      пилюля держала «0/2» после того, как в голос вошли. Опрашивает только тот, кто НЕ в голосе:
      изнутри комнаты правду говорит живой состав.
    */
    poll: true,
  });

  /*
    Состав голоса нужен для общего счётчика. Ключ тот же, что внутри
    `useVoiceRoomState`: это чтение того же кеша, а не второй запрос.
  */
  const { data: snapshot } = useQuery({
    ...getVoiceRoomQueryOptions(session.sessionId),
    enabled: isPlatform,
  });

  const liveMembers = useVoiceStore((s) => s.members);
  const micEnabled = useVoiceStore((s) => s.micEnabled);
  /*
    Устройства и разрешение на микрофон принадлежат владельцу комнаты, а не пилюле: свой вызов
    хука дал бы кнопки с пустой ссылкой на комнату – выбор устройства и рация вызывались бы и
    молча ничего не делали. Провайдер стоит на layout-роуте, то есть выше и пилюли, и окна.
  */
  const {
    micDenied,
    micLevel,
    microphoneTrack,
    activeDeviceIds,
    refreshDeviceIds,
    requestMicAccess,
    listDevices,
    switchDevice,
  } = useVoiceRoomOwner('SessionRoomPill');
  const [devicePickerOpen, setDevicePickerOpen] = useState(false);
  const { join, pending } = useVoiceActions();

  const accepted = useMemo(
    () =>
      session.members
        .filter((member) => member.status === 'ACCEPTED')
        .map((member) => ({
          userId: member.memberId,
          name: member.displayName,
          avatar: member.avatar,
        })),
    [session.members]
  );

  /*
    Слияние состава и голоса – та же чистая функция, что в ярусе. Пилюля обязана считать «сколько
    нас» ровно так же, как окно: иначе свернуть комнату значило бы получить другой ответ на тот
    же вопрос.
  */
  const merged = useMemo(
    () =>
      mergeRosterVoice(
        { members: accepted, hostId: session.hostId },
        isPlatform ? (inThisRoom ? liveMembers : (snapshot?.participants ?? [])) : []
      ),
    [accepted, session.hostId, isPlatform, inThisRoom, liveMembers, snapshot?.participants]
  );

  /** Имя хозяина знает состав: у комнаты голоса его нет до входа. */
  const hostName =
    session.members.find((member) => member.memberId === session.hostId)?.displayName ?? null;

  const closed = isClosedPhase(phase);
  /*
    Сколько человек сидит в голосовом канале Discord прямо сейчас. Спрашиваем только у
    Discord-сессий: у остальных канала нет. Запрос общий с ярусом и карточкой заявки – ключ один,
    поэтому вкладка спрашивает сервер один раз.
  */
  const { data: discordRoom } = useQuery({
    ...getDiscordRoomQueryOptions({ id: session.postId }),
    enabled: state === 'discord',
  });
  const discordInvite = discordRoom?.channelInviteLink ?? null;
  /*
    Дверь в канал - общий помощник: без привязанного Discord ссылка ведёт в закрытый канал, и
    человек упирается в него без объяснения. Тот же помощник у яруса и полосы.
  */
  const door = useDiscordChannelDoor(discordInvite);
  const discordConnected =
    state === 'discord' && discordRoom
      ? (discordRoom.realtimeMembers?.filter(isDefined).length ?? 0)
      : null;

  /*
    Счёт молчит там, где числа у нас нет: у закончившейся сессии и у Discord, пока ответ о канале
    не приехал. Ноль в этих случаях был бы не сдержанностью, а неправдой.

    Выключенный голос сюда больше не относится (20.09.2026): числа у него нет только про голос, а
    про состав есть, и с отдельным значком «5/6» уже не прочитается как «слышно пятерых» – раньше
    прочиталось бы, потому что дробь в этой строке стояла голой.
  */
  const count = tierCountLine({
    state,
    connected: merged.connected,
    accepted: merged.total,
    teamLimit: session.teamLimit ?? 0,
    discordConnected,
  });
  // Пока Discord ещё не ответил, число неизвестно. Состав здесь не подменяет голос: тот же слот
  // не должен менять смысл во время загрузки.
  const showVoiceCount = !closed && !(state === 'discord' && discordConnected === null);
  const roomStatus =
    snapshot === undefined ? session.voiceRoomStatus : (snapshot?.status ?? 'CLOSED');
  const slot = resolvePillVoiceSlot(state, phase, roomStatus);
  const density =
    useBreakpointValue<'desktop' | 'mobile'>({ base: 'mobile', md: 'desktop' }) ?? 'mobile';
  const SLOT = density === 'mobile' ? '44px' : '40px';
  const now = useRoomClock(phase);
  const startsIn = phase === 'upcoming' ? formatStartsIn(session.startedAt, now) : null;
  const phaseLabel = closed
    ? phase === 'cancelled'
      ? t`Session cancelled`
      : t`post:session.room.ended`
    : startsIn
      ? t`post:session.room.startsIn ${startsIn}`
      : t`Session is on`;
  const attach = useCallback(
    (node: HTMLDivElement | null) => {
      pillRef.current = node;
      onNodeChange?.(node);
    },
    [onNodeChange]
  );

  return (
    <RoomSurface
      ref={attach}
      {...floatingBarSurface}
      data-testid={surface === 'bar' ? 'session-room-bar' : 'session-room-pill'}
      data-phase={phase}
      data-voice-state={state}
      data-anchor={surface === 'bar' ? mobileAnchor : undefined}
      {...(surface === 'bar'
        ? { left: 3, right: 3, ...MOBILE_ANCHOR_PROPS[mobileAnchor] }
        : CORNER_PROPS[corner])}
      maxW={
        surface === 'bar'
          ? undefined
          : slot === 'mic'
            ? 'min(374px, calc(100vw - 24px))'
            : 'min(300px, calc(100vw - 24px))'
      }
      direction="row"
      align="center"
      px={2}
      py={1}
      gap={2}
      flexWrap="wrap"
      {...(surface === 'bar'
        ? {
            onPointerDown: onGripPointerDown,
            cursor: pinned ? 'default' : 'grab',
            css: { touchAction: 'none' },
          }
        : {})}
    >
      <Flex
        align="center"
        gap={2}
        minW="max-content"
        flex={1}
        {...(surface === 'bar'
          ? {}
          : { onPointerDown: onGripPointerDown, cursor: 'grab', css: { touchAction: 'none' } })}
        aria-label={phaseLabel}
        title={phaseLabel}
      >
        {session.game?.icon ? (
          <Box data-testid={`session-room-${surface}-game`} flexShrink={0}>
            <SessionRoomGameLogo
              icon={session.game.icon}
              color={session.game.iconColor}
              status={phase === 'live' ? 'live' : closed ? 'over' : 'upcoming'}
            />
          </Box>
        ) : null}
        {phase !== 'live' && slot !== 'mic' ? (
          <Box data-testid={`session-room-${surface}-state`} flexShrink={0}>
            <RoomCompactStatus
              closed={closed}
              cancelled={phase === 'cancelled'}
              startsIn={startsIn}
            />
          </Box>
        ) : null}
        <Box data-testid={`session-room-${surface}-count`} flexShrink={0}>
          <TierCounts
            accepted={merged.total}
            capacity={(session.teamLimit ?? 0) > 0 ? session.teamLimit! : merged.total}
            count={count}
            compact
            inline
            showVoice={showVoiceCount || slot !== 'none'}
          />
        </Box>
      </Flex>
      <Flex align="center" gap={2} flexShrink={0} ml="auto">
        <Tooltip
          content={t`post:session.room.chatCreating`}
          disabled={Boolean(onFullscreen)}
          openDelay={200}
        >
          <Box position="relative" flexShrink={0}>
            <IconButton
              variant="ghost"
              aria-label={onFullscreen ? t`Open chat` : t`post:session.room.chatCreating`}
              w={SLOT}
              minW={SLOT}
              h={SLOT}
              color="fg.icon"
              disabled={!onFullscreen}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                trackRoomChatOpened(density === 'mobile' ? 'sheet' : 'window');
                if (density === 'mobile' && session.chatId) requestComposerFocus(session.chatId);
                onFullscreen?.();
              }}
            >
              {onFullscreen ? <CommentIcon /> : <Spinner size="sm" />}
            </IconButton>
            {session.unreadCount > 0 ? (
              <Box position="absolute" top={0.5} right={0.5} pointerEvents="none">
                <UnreadBadge count={session.unreadCount} size="sm" />
              </Box>
            ) : null}
          </Box>
        </Tooltip>
        {slot === 'mic' && state === 'joined' ? (
          /*
            Пока я в голосе, здесь стоит тот же кластер, что в раскрытой комнате: микрофон,
            выбор устройства и выход (16.09.2026).

            11.09 слот держал один выход, а микрофон считался вопросом «внутри разговора» и
            оставался в раскрытом окне. Рассуждение было о месте, а не о человеке: заглушить
            себя – самое срочное действие в голосе (кто-то вошёл в комнату, залаяла собака), и
            оно единственное требовало сперва развернуть окно. Выход при этом никуда не делся –
            он в этом же кластере, той же кнопкой, что и в окне.

            Кластер взят целиком, а не собран заново: своя копия микрофона в пилюле знала бы
            половину правды (рация, «доступа нет»), и одно и то же состояние выглядело бы на двух
            поверхностях по-разному – ровно та беда, ради которой `MicButton` и свели в один
            компонент.

            Нажатия внутри кластера не должны разворачивать комнату: обработчик тела пилюли
            сработал бы следом, и человек получил бы и заглушённый микрофон, и раскрытое окно
            поверх. Поэтому клики гасятся на обёртке, а не в каждой кнопке кластера.
          */
          <Box flexShrink={0} onClick={(event) => event.stopPropagation()}>
            <TierMicCluster
              density={density}
              micEnabled={micEnabled}
              micDenied={micDenied}
              onToggleMic={() => void requestVoiceMicrophone(!micEnabled)}
              onRequestAccess={() => {
                // Доступ дали – сразу показываем устройства: следующий вопрос человека всегда «а
                // тем ли микрофоном я говорю».
                void (async () => {
                  if (await requestMicAccess()) {
                    setDevicePickerOpen(true);
                    return;
                  }
                  /*
                    Отказ. Браузер второй раз не спрашивает - запрет уже стоит, и повторный
                    вызов возвращает его же молча: человек жал кнопку, и ничего не менялось ни
                    разу. Разворачиваем комнату - там, в ярусе голоса, стоит готовое объяснение
                    («доступ к микрофону запрещён», в приложении ещё и кнопка «Открыть
                    настройки»). Так же ведёт себя полоса над лентой.
                  */
                  expand();
                })();
              }}
              micLevel={micLevel}
              microphoneTrack={microphoneTrack}
              listDevices={listDevices}
              onSelectDevice={switchDevice}
              activeDeviceIds={activeDeviceIds}
              onOpenDevices={refreshDeviceIds}
              devicePickerOpen={devicePickerOpen}
              onDevicePickerOpenChange={setDevicePickerOpen}
              onLeave={() => void leaveVoice(queryClient)}
            />
          </Box>
        ) : slot === 'mic' ? (
          /*
            Соединение ещё собирается (`connecting`/`reconnecting`): микрофона здесь быть не
            может – комнаты, которой он принадлежит, пока нет. Выход при этом нужен именно
            сейчас: чаще всего уходят как раз из звонка, который не складывается.
          */
          <IconButton
            variant="ghost"
            aria-label={t`post:session.room.leaveVoice`}
            w={SLOT}
            minW={SLOT}
            h={SLOT}
            // Цветом не выделяем – ровно как в кебабе окна: красным там помечен только выход из
            // СОСТАВА, после которого место могут занять. Из голоса возвращаются свободно.
            color="fg.icon"
            onClick={(event) => {
              // Тело пилюли разворачивает комнату: без этого человек и вышел бы, и получил
              // раскрытое окно поверх.
              event.stopPropagation();
              void leaveVoice(queryClient);
            }}
          >
            <Icon transform="rotate(135deg)">
              <PhoneIcon />
            </Icon>
          </IconButton>
        ) : slot === 'discord' ? (
          /*
            Голос этой сессии живёт в гильдии, и единственное действие здесь – уйти туда.
            Ссылкой, а не обработчиком: у канала есть адрес, и «открыть в новой вкладке» средней
            кнопкой – ровно то, чего человек ждёт от этого значка.
          */
          door.kind === 'none' ? null : (
            <IconButton
              variant="ghost"
              asChild
              aria-label={
                door.kind === 'link' ? t`post:lobby.linkDiscord` : t`post:voice.room.toDiscord`
              }
              w={SLOT}
              minW={SLOT}
              h={SLOT}
              color="fg.icon"
            >
              {door.kind === 'channel' ? (
                <a
                  href={door.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                >
                  <PhoneIcon />
                </a>
              ) : (
                <DiscordLinkAnchor stopPropagation>
                  <DiscordIcon />
                </DiscordLinkAnchor>
              )}
            </IconButton>
          )
        ) : slot === 'none' ? null : slot === 'return' ? (
          /*
            Меня выбило из голоса, а не «я туда не заходил»: страницу перезагрузили или вкладку
            открыли заново, соединение с медиасервером после этого не восстанавливается
            сознательно. «Войти» тут было бы неправдой – войти человек уже входил, и разговор
            идёт без него.

            В свёрнутом виде слово уезжает в `aria-label`: подпись «Вернуться» – 105 пикселей
            против 40 у микрофона, и эта разница доставалась не кнопке, а первой строке –
            остаток времени исчезал целиком. Слово при этом никуда не делось: оно то же и из
            того же ключа, а в ярусе развёрнутой комнаты стоит кнопка с настоящей подписью.
          */
          <RoomVoiceReturnIcon
            sessionId={session.sessionId}
            gameName={session.game?.name}
            hostName={hostName}
            size={SLOT}
            otherDevice={state === 'otherTab'}
          />
        ) : (
          /*
            Значок, а не подпись (12.09.2026). Слот шириной 40 стоит в ряду с переписко́й и
            выходом, и подпись рядом с ними делала вход предметом другого веса, а заодно
            съедала у первой строки те самые 32 пикселя, из-за которых пропадал остаток
            времени. Возвращение по соседству уже было значком – теперь оба состояния входа
            выглядят одинаково, и одинаково же с окном, где кнопка тоже значок.

            Слово живёт в `aria-label` и в карточке на странице заявки, где место есть.

            Трубка, залитая фиолетовым (15.09.2026): закрашенный микрофон читался как «я уже в
            голосе» – после входа здесь же встаёт микрофон. См. `VoiceRoomBlock`.
          */
          <IconButton
            variant="solid"
            colorPalette="purple"
            aria-label={t`post:session.room.joinVoice`}
            title={t`post:session.room.joinVoice`}
            w={SLOT}
            minW={SLOT}
            h={SLOT}
            loading={pending}
            onClick={(event) => {
              // Тело пилюли разворачивает комнату: без этого человек и вошёл бы, и получил
              // раскрытое окно поверх.
              event.stopPropagation();
              void join(session.sessionId, {
                gameName: session.game?.name,
                // Имя хозяина знает состав: у комнаты голоса его нет до входа.
                hostName,
              }).catch(() => undefined);
            }}
          >
            <PhoneIcon />
          </IconButton>
        )}
        {closed && !inThisRoom && slot === 'none' ? (
          <IconButton
            variant="ghost"
            aria-label={t`post:session.room.close`}
            boxSize={SLOT}
            minW={SLOT}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
        <RoomModeButton mode="expand" onClick={expand} />
      </Flex>
    </RoomSurface>
  );
};
